import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ViewChild, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { EzFormInputType, InputConfig } from "@template-ui/types/ez-form.types";
import { InputSelectOption } from "@template-ui/types";
import { RoleService } from "@app/shared/services/roles.service";
import { Subscription } from "rxjs";
import {
  FeatureFlagAdminDisplayError,
  FeatureFlagDefinitionDetail,
  FeatureFlagDefinitionFormValues,
  FeatureFlagDefinitionSummary,
  FeatureFlagDefinitionUpdateRequest,
  FeatureFlagRollout,
  FeatureFlagRolloutFormValues,
  FeatureFlagRolloutUpsertRequest,
} from "./platform-feature-flag-admin.models";
import {
  PlatformFeatureFlagAdminService,
  mapFeatureFlagAdminError,
} from "./platform-feature-flag-admin.service";

const PLATFORM_ACCESS_ADMIN_READ = "platform_access_admin.read";
const PLATFORM_ACCESS_ADMIN_AUTHOR = "platform_access_admin.author";
const PLATFORM_ACCESS_ADMIN_PUBLISH = "platform_access_admin.publish";
const PLATFORM_ACCESS_ADMIN_ROLLBACK = "platform_access_admin.rollback";

const ENABLED_STATE_OPTIONS: InputSelectOption[] = [
  { value: "enabled", viewValue: "Enabled" },
  { value: "disabled", viewValue: "Disabled" },
];

@Component({
    selector: "phms-platform-feature-flag-admin",
    imports: [CommonModule, UiLibraryModule],
    templateUrl: "./platform-feature-flag-admin.component.html",
    styleUrl: "./platform-feature-flag-admin.component.scss"
})
export class PlatformFeatureFlagAdminComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagAdminService = inject(PlatformFeatureFlagAdminService);
  private readonly roles = inject(RoleService);
  private catalogLoadSubscription?: Subscription;
  private catalogLoadRequestId = 0;
  private definitionLoadSubscription?: Subscription;
  private definitionLoadRequestId = 0;

  @ViewChild("definitionForm")
  private definitionForm?: EzFormComponent;

  @ViewChild("rolloutForm")
  private rolloutForm?: EzFormComponent;

  readonly canRead = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_READ);
  readonly canAuthor = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_AUTHOR);
  readonly canPublish = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_PUBLISH);
  readonly canRollback = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_ROLLBACK);

  readonly isCatalogLoading = signal(true);
  readonly isDefinitionLoading = signal(false);
  readonly isDefinitionSaving = signal(false);
  readonly isRolloutSaving = signal(false);
  readonly activeActionKey = signal<string | null>(null);
  readonly definitions = signal<FeatureFlagDefinitionSummary[]>([]);
  readonly selectedDefinition = signal<FeatureFlagDefinitionDetail | null>(null);
  readonly isCreatingDefinition = signal(false);
  readonly editingRolloutId = signal<number | null>(null);
  readonly successMessage = signal<string | null>(null);
  readonly errorState = signal<FeatureFlagAdminDisplayError | null>(null);

  readonly sortedRollouts = computed(() =>
    [...(this.selectedDefinition()?.rollouts ?? [])].sort((left, right) => {
      if (left.precedenceRank !== right.precedenceRank) {
        return left.precedenceRank - right.precedenceRank;
      }

      return new Date(right.effectiveStartUtc).getTime() - new Date(left.effectiveStartUtc).getTime();
    }),
  );

  readonly definitionEditBlockedReason = computed(() => {
    if (this.isCreatingDefinition()) {
      return null;
    }

    const definition = this.selectedDefinition();
    if (!definition) {
      return "Select a feature flag before editing its definition.";
    }

    switch (normalizeCode(definition.statusCode)) {
      case "active":
        return "Deactivate this feature flag before editing its definition.";
      case "retired":
        return "Retired feature flags cannot be edited.";
      default:
        return null;
    }
  });

  readonly rolloutEditBlockedReason = computed(() => {
    const definition = this.selectedDefinition();
    if (!definition) {
      return "Select a feature flag before authoring a rollout.";
    }

    return normalizeCode(definition.statusCode) === "retired"
      ? "Retired feature flags cannot accept rollout changes."
      : null;
  });

  definitionFormConfig: InputConfig[] = this.buildDefinitionFormConfig(true, false);
  rolloutFormConfig: InputConfig[] = this.buildRolloutFormConfig();

  definitionFormValues: FeatureFlagDefinitionFormValues = this.createEmptyDefinitionFormValues();
  rolloutFormValues: FeatureFlagRolloutFormValues = this.createEmptyRolloutFormValues();

  constructor() {
    this.loadDefinitions();
  }

  get selectedFlagCode(): string | null {
    return this.selectedDefinition()?.flagCode ?? null;
  }

  get selectedDefinitionStatusLabel(): string {
    return this.toStatusLabel(this.selectedDefinition()?.statusCode);
  }

  loadDefinitions(preferredFlagCode?: string): void {
    this.clearMessages();
    this.isCatalogLoading.set(true);
    this.catalogLoadSubscription?.unsubscribe();
    const requestId = ++this.catalogLoadRequestId;

    this.catalogLoadSubscription = this.featureFlagAdminService
      .listDefinitions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (definitions) => {
          if (requestId !== this.catalogLoadRequestId) {
            return;
          }

          const sortedDefinitions = [...definitions].sort(compareDefinitions);
          this.definitions.set(sortedDefinitions);
          this.isCatalogLoading.set(false);

          if (this.isCreatingDefinition()) {
            return;
          }

          if (sortedDefinitions.length === 0) {
            this.beginCreateDefinition(false);
            return;
          }

          const nextFlagCode = preferredFlagCode && sortedDefinitions.some(
            (definition) => normalizeCode(definition.flagCode) === normalizeCode(preferredFlagCode),
          )
            ? preferredFlagCode
            : (this.selectedFlagCode && sortedDefinitions.some(
                (definition) => normalizeCode(definition.flagCode) === normalizeCode(this.selectedFlagCode),
              )
                ? this.selectedFlagCode
                : sortedDefinitions[0].flagCode);
          if (nextFlagCode) {
            this.selectDefinition(nextFlagCode, false);
          }
        },
        error: (error) => {
          if (requestId !== this.catalogLoadRequestId) {
            return;
          }

          this.definitions.set([]);
          this.isCatalogLoading.set(false);
          this.errorState.set(mapFeatureFlagAdminError(error));
        },
      });
  }

  beginCreateDefinition(clearMessages = true): void {
    if (clearMessages) {
      this.clearMessages();
    }

    this.cancelDefinitionLoad();
    this.isCreatingDefinition.set(true);
    this.selectedDefinition.set(null);
    this.definitionFormValues = this.createEmptyDefinitionFormValues();
    this.definitionFormConfig = this.buildDefinitionFormConfig(true, false);
    this.definitionForm?.clearErrors();
    this.resetRolloutEditor(false);
  }

  selectDefinition(flagCode: string, clearMessages = true): void {
    if (clearMessages) {
      this.clearMessages();
    }

    this.isCreatingDefinition.set(false);
    this.requestDefinition(flagCode, { showLoading: true });
  }

  saveDefinition(): void {
    if (!this.canAuthor()) {
      this.errorState.set({
        title: "Access denied",
        messages: ["You do not have permission to author platform feature flags."],
        issueCodes: [],
      });
      return;
    }

    if (this.definitionEditBlockedReason()) {
      this.errorState.set({
        title: "Definition is read-only",
        messages: [this.definitionEditBlockedReason()!],
        issueCodes: [],
      });
      return;
    }

    this.clearMessages();
    if (!this.ensureFormIsValid(this.definitionForm, "Resolve the feature flag form errors before saving.")) {
      return;
    }

    const request = this.buildDefinitionUpdateRequest(this.definitionFormValues);
    const isCreateMode = this.isCreatingDefinition();
    this.isDefinitionSaving.set(true);

    const request$ = isCreateMode
      ? this.featureFlagAdminService.createDefinition({
          flagCode: this.definitionFormValues.flagCode.trim(),
          ...request,
        })
      : this.featureFlagAdminService.updateDefinition(this.selectedFlagCode!, request);

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (definition) => {
          this.applyDefinition(definition);
          this.successMessage.set(
            isCreateMode
              ? "Feature flag created successfully."
              : "Feature flag updated successfully.",
          );
          this.isDefinitionSaving.set(false);
        },
        error: (error) => {
          this.isDefinitionSaving.set(false);
          this.errorState.set(mapFeatureFlagAdminError(error));
        },
      });
  }

  activateSelectedDefinition(): void {
    if (!this.selectedFlagCode) {
      return;
    }

    this.clearMessages();
    this.activeActionKey.set("definition:activate");

    this.featureFlagAdminService
      .activateDefinition(this.selectedFlagCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (definition) => {
          this.applyDefinition(definition);
          this.successMessage.set("Feature flag activated.");
          this.activeActionKey.set(null);
        },
        error: (error) => {
          this.activeActionKey.set(null);
          this.errorState.set(mapFeatureFlagAdminError(error));
        },
      });
  }

  deactivateSelectedDefinition(): void {
    if (!this.selectedFlagCode) {
      return;
    }

    this.clearMessages();
    this.activeActionKey.set("definition:deactivate");

    this.featureFlagAdminService
      .deactivateDefinition(this.selectedFlagCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (definition) => {
          this.applyDefinition(definition);
          this.successMessage.set("Feature flag deactivated.");
          this.activeActionKey.set(null);
        },
        error: (error) => {
          this.activeActionKey.set(null);
          this.errorState.set(mapFeatureFlagAdminError(error));
        },
      });
  }

  startCreateRollout(): void {
    const blockedReason = this.rolloutEditBlockedReason();
    if (blockedReason) {
      this.errorState.set({
        title: "Rollout authoring unavailable",
        messages: [blockedReason],
        issueCodes: [],
      });
      return;
    }

    this.clearMessages();
    this.resetRolloutEditor(false);
  }

  editRollout(rollout: FeatureFlagRollout): void {
    const normalizedStatus = normalizeCode(rollout.statusCode);
    if (normalizedStatus === "published") {
      this.errorState.set({
        title: "Rollout is read-only",
        messages: ["Published rollouts must be rolled back before they can be edited."],
        issueCodes: ["feature_flag.rollout.published"],
      });
      return;
    }

    this.clearMessages();
    this.editingRolloutId.set(rollout.id);
    this.rolloutFormValues = this.mapRolloutToFormValues(rollout);
    this.rolloutForm?.clearErrors();
  }

  saveRollout(): void {
    if (!this.canAuthor()) {
      this.errorState.set({
        title: "Access denied",
        messages: ["You do not have permission to author rollout changes."],
        issueCodes: [],
      });
      return;
    }

    if (!this.selectedFlagCode) {
      this.errorState.set({
        title: "Feature flag required",
        messages: ["Select a feature flag before authoring a rollout."],
        issueCodes: [],
      });
      return;
    }

    const blockedReason = this.rolloutEditBlockedReason();
    if (blockedReason) {
      this.errorState.set({
        title: "Rollout authoring unavailable",
        messages: [blockedReason],
        issueCodes: [],
      });
      return;
    }

    this.clearMessages();
    if (!this.ensureFormIsValid(this.rolloutForm, "Resolve the rollout form errors before saving.")) {
      return;
    }

    const normalizedRolloutDates = this.normalizeRolloutDates(this.rolloutFormValues);
    const customValidationMessages = this.validateRolloutFormValues(
      this.rolloutFormValues,
      normalizedRolloutDates.effectiveStartUtc,
      normalizedRolloutDates.effectiveEndUtc,
    );
    if (customValidationMessages.length > 0) {
      this.errorState.set({
        title: "Rollout validation failed",
        messages: customValidationMessages,
        issueCodes: [],
      });
      return;
    }

    const effectiveStartUtc = normalizedRolloutDates.effectiveStartUtc;
    if (!effectiveStartUtc) {
      this.errorState.set({
        title: "Rollout validation failed",
        messages: ["Effective start must be a valid date and time."],
        issueCodes: [],
      });
      return;
    }

    const request = this.buildRolloutUpsertRequest(
      this.rolloutFormValues,
      effectiveStartUtc,
      normalizedRolloutDates.effectiveEndUtc,
    );
    const isEditingExistingRollout = this.editingRolloutId() !== null;
    this.isRolloutSaving.set(true);

    const request$ = this.editingRolloutId()
      ? this.featureFlagAdminService.updateRollout(this.selectedFlagCode, this.editingRolloutId()!, request)
      : this.featureFlagAdminService.createRollout(this.selectedFlagCode, request);

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isRolloutSaving.set(false);
          this.resetRolloutEditor(false);
          this.reloadSelectedDefinition(
            isEditingExistingRollout
              ? "Rollout updated successfully."
              : "Rollout created successfully.",
          );
        },
        error: (error) => {
          this.isRolloutSaving.set(false);
          this.errorState.set(mapFeatureFlagAdminError(error));
        },
      });
  }

  publishRollout(rollout: FeatureFlagRollout): void {
    if (!this.selectedFlagCode) {
      return;
    }

    this.clearMessages();
    this.activeActionKey.set(`rollout:publish:${rollout.id}`);

    this.featureFlagAdminService
      .publishRollout(this.selectedFlagCode, rollout.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.activeActionKey.set(null);
          this.reloadSelectedDefinition("Rollout published successfully.");
        },
        error: (error) => {
          this.activeActionKey.set(null);
          this.errorState.set(mapFeatureFlagAdminError(error));
        },
      });
  }

  rollbackRollout(rollout: FeatureFlagRollout): void {
    if (!this.selectedFlagCode) {
      return;
    }

    this.clearMessages();
    this.activeActionKey.set(`rollout:rollback:${rollout.id}`);

    this.featureFlagAdminService
      .rollbackRollout(this.selectedFlagCode, rollout.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.activeActionKey.set(null);
          this.reloadSelectedDefinition("Rollout rolled back successfully.");
        },
        error: (error) => {
          this.activeActionKey.set(null);
          this.errorState.set(mapFeatureFlagAdminError(error));
        },
      });
  }

  resetDefinitionForm(): void {
    this.definitionForm?.clearErrors();
    this.errorState.set(null);

    if (this.isCreatingDefinition()) {
      this.definitionFormValues = this.createEmptyDefinitionFormValues();
      return;
    }

    const definition = this.selectedDefinition();
    if (definition) {
      this.definitionFormValues = this.mapDefinitionToFormValues(definition);
    }
  }

  resetRolloutForm(): void {
    this.resetRolloutEditor();
  }

  isActionBusy(actionKey: string): boolean {
    return this.activeActionKey() === actionKey;
  }

  canActivateDefinition(): boolean {
    const definition = this.selectedDefinition();
    const normalizedStatus = normalizeCode(definition?.statusCode);
    return !!definition && normalizedStatus.length > 0 && normalizedStatus !== "active" && normalizedStatus !== "retired";
  }

  canDeactivateDefinition(): boolean {
    return normalizeCode(this.selectedDefinition()?.statusCode) === "active";
  }

  canEditRollout(rollout: FeatureFlagRollout): boolean {
    return normalizeCode(rollout.statusCode) !== "published" && !this.rolloutEditBlockedReason();
  }

  canPublishRollout(rollout: FeatureFlagRollout): boolean {
    return normalizeCode(rollout.statusCode) !== "published";
  }

  canRollbackPublishedRollout(rollout: FeatureFlagRollout): boolean {
    return normalizeCode(rollout.statusCode) === "published";
  }

  toStatusLabel(statusCode: string | null | undefined): string {
    const normalized = normalizeCode(statusCode);
    if (normalized.length === 0) {
      return "Unknown";
    }

    return normalized
      .split("_")
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(" ");
  }

  private applyDefinition(definition: FeatureFlagDefinitionDetail): void {
    this.selectedDefinition.set(definition);
    this.isCreatingDefinition.set(false);
    this.definitionFormValues = this.mapDefinitionToFormValues(definition);
    this.definitionFormConfig = this.buildDefinitionFormConfig(
      false,
      this.definitionEditBlockedReason() !== null,
    );
    this.definitionForm?.clearErrors();
    this.resetRolloutEditor(false);
    this.upsertDefinitionSummary(definition);
  }

  private reloadSelectedDefinition(successMessage: string): void {
    const flagCode = this.selectedFlagCode;
    if (!flagCode) {
      return;
    }

    this.requestDefinition(flagCode, { showLoading: false, successMessage });
  }

  private requestDefinition(
    flagCode: string,
    options: { showLoading: boolean; successMessage?: string },
  ): void {
    this.definitionLoadSubscription?.unsubscribe();
    const requestId = ++this.definitionLoadRequestId;
    this.isDefinitionLoading.set(options.showLoading);

    this.definitionLoadSubscription = this.featureFlagAdminService
      .getDefinition(flagCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (definition) => {
          if (requestId !== this.definitionLoadRequestId) {
            return;
          }

          this.applyDefinition(definition);
          this.isDefinitionLoading.set(false);

          if (options.successMessage) {
            this.successMessage.set(options.successMessage);
          }
        },
        error: (error) => {
          if (requestId !== this.definitionLoadRequestId) {
            return;
          }

          this.isDefinitionLoading.set(false);
          this.errorState.set(mapFeatureFlagAdminError(error));
        },
      });
  }

  private cancelDefinitionLoad(): void {
    this.definitionLoadSubscription?.unsubscribe();
    this.definitionLoadSubscription = undefined;
    this.definitionLoadRequestId++;
    this.isDefinitionLoading.set(false);
  }

  private upsertDefinitionSummary(definition: FeatureFlagDefinitionSummary): void {
    const summary: FeatureFlagDefinitionSummary = {
      id: definition.id,
      flagCode: definition.flagCode,
      displayName: definition.displayName,
      description: definition.description,
      flagTypeCode: definition.flagTypeCode,
      defaultEnabled: definition.defaultEnabled,
      statusCode: definition.statusCode,
      ownerCode: definition.ownerCode,
    };

    const nextDefinitions = [...this.definitions()];
    const existingIndex = nextDefinitions.findIndex(
      (item) => normalizeCode(item.flagCode) === normalizeCode(summary.flagCode),
    );

    if (existingIndex >= 0) {
      nextDefinitions.splice(existingIndex, 1, summary);
    } else {
      nextDefinitions.push(summary);
    }

    nextDefinitions.sort(compareDefinitions);
    this.definitions.set(nextDefinitions);
  }

  private ensureFormIsValid(form: EzFormComponent | undefined, fallbackMessage: string): boolean {
    const formGroup = form?.theFormGroup;
    if (!formGroup || formGroup.valid) {
      return true;
    }

    formGroup.markAllAsTouched();
    const controlErrors = form?.controlErrors?.map((entry) => entry.message) ?? [];
    this.errorState.set({
      title: "Validation failed",
      messages: controlErrors.length > 0 ? [...new Set(controlErrors)] : [fallbackMessage],
      issueCodes: [],
    });

    return false;
  }

  private buildDefinitionFormConfig(isCreateMode: boolean, readOnly: boolean): InputConfig[] {
    return [
      {
        name: "flagCode",
        type: EzFormInputType.TEXT,
        label: "Flag Code",
        row: 1,
        width: "1x",
        readOnly: !isCreateMode,
        validation: isCreateMode ? [Validators.required] : [],
        validationMessages: {
          required: "Flag code is required.",
        },
      },
      {
        name: "displayName",
        type: EzFormInputType.TEXT,
        label: "Display Name",
        row: 1,
        width: "1x",
        readOnly,
        validation: [Validators.required],
        validationMessages: {
          required: "Display name is required.",
        },
      },
      {
        name: "description",
        type: EzFormInputType.TEXTAREA,
        label: "Description",
        row: 2,
        width: "fill",
        readOnly,
        textAreaRows: 4,
      },
      {
        name: "flagTypeCode",
        type: EzFormInputType.TEXT,
        label: "Flag Type Code",
        row: 3,
        width: "1x",
        readOnly,
        validation: [Validators.required],
        validationMessages: {
          required: "Flag type code is required.",
        },
      },
      {
        name: "ownerCode",
        type: EzFormInputType.TEXT,
        label: "Owner Code",
        row: 3,
        width: "1x",
        readOnly,
        validation: [Validators.required],
        validationMessages: {
          required: "Owner code is required.",
        },
      },
      {
        name: "defaultEnabled",
        type: EzFormInputType.CHECKBOX,
        label: "Default Enabled",
        row: 4,
        width: "1x",
        readOnly,
      },
    ];
  }

  private buildRolloutFormConfig(): InputConfig[] {
    return [
      {
        name: "scopeTypeCode",
        type: EzFormInputType.TEXT,
        label: "Scope Type Code",
        row: 1,
        width: "1x",
        validation: [Validators.required],
        validationMessages: {
          required: "Scope type code is required.",
        },
      },
      {
        name: "scopeKey",
        type: EzFormInputType.TEXT,
        label: "Scope Key",
        row: 1,
        width: "1x",
      },
      {
        name: "audienceCode",
        type: EzFormInputType.TEXT,
        label: "Audience Code",
        row: 2,
        width: "1x",
      },
      {
        name: "environmentCode",
        type: EzFormInputType.TEXT,
        label: "Environment Code",
        row: 2,
        width: "1x",
      },
      {
        name: "enabledStateCode",
        type: EzFormInputType.SELECT,
        label: "Enabled State",
        row: 3,
        width: "1x",
        options: ENABLED_STATE_OPTIONS,
        validation: [Validators.required],
        validationMessages: {
          required: "Enabled state is required.",
        },
      },
      {
        name: "precedenceRank",
        type: EzFormInputType.NUMBER,
        label: "Precedence",
        row: 3,
        width: "1x",
        validation: [Validators.required, Validators.min(0)],
        validationMessages: {
          required: "Precedence is required.",
          min: "Precedence must be zero or greater.",
        },
      },
      {
        name: "rollbackPostureCode",
        type: EzFormInputType.TEXT,
        label: "Rollback Posture Code",
        row: 4,
        width: "1x",
        validation: [Validators.required],
        validationMessages: {
          required: "Rollback posture code is required.",
        },
      },
      {
        name: "effectiveStartUtc",
        type: EzFormInputType.DATE_TIME,
        label: "Effective Start (Local Time)",
        row: 5,
        width: "1x",
        customTooltip: "Displayed in local time and saved back to the API as UTC.",
        validation: [Validators.required],
        validationMessages: {
          required: "Effective start is required.",
        },
        dateTimeConfig: {
          hourFormat: "24",
          showSeconds: false,
          showClear: false,
        },
      },
      {
        name: "effectiveEndUtc",
        type: EzFormInputType.DATE_TIME,
        label: "Effective End (Local Time)",
        row: 5,
        width: "1x",
        customTooltip: "Leave blank for an open-ended rollout window.",
        dateTimeConfig: {
          hourFormat: "24",
          showSeconds: false,
          showClear: true,
        },
      },
    ];
  }

  private createEmptyDefinitionFormValues(): FeatureFlagDefinitionFormValues {
    return {
      flagCode: "",
      displayName: "",
      description: "",
      flagTypeCode: "",
      defaultEnabled: false,
      ownerCode: "platform",
    };
  }

  private createEmptyRolloutFormValues(): FeatureFlagRolloutFormValues {
    return {
      scopeTypeCode: "",
      scopeKey: "",
      audienceCode: "",
      environmentCode: "",
      enabledStateCode: "disabled",
      precedenceRank: 0,
      rollbackPostureCode: "",
      effectiveStartUtc: new Date(),
      effectiveEndUtc: null,
    };
  }

  private mapDefinitionToFormValues(definition: FeatureFlagDefinitionDetail): FeatureFlagDefinitionFormValues {
    return {
      flagCode: definition.flagCode,
      displayName: definition.displayName ?? "",
      description: definition.description ?? "",
      flagTypeCode: definition.flagTypeCode ?? "",
      defaultEnabled: definition.defaultEnabled,
      ownerCode: definition.ownerCode ?? "",
    };
  }

  private mapRolloutToFormValues(rollout: FeatureFlagRollout): FeatureFlagRolloutFormValues {
    return {
      scopeTypeCode: rollout.scopeTypeCode ?? "",
      scopeKey: rollout.scopeKey ?? "",
      audienceCode: rollout.audienceCode ?? "",
      environmentCode: rollout.environmentCode ?? "",
      enabledStateCode: rollout.enabledStateCode ?? "disabled",
      precedenceRank: rollout.precedenceRank,
      rollbackPostureCode: rollout.rollbackPostureCode ?? "",
      effectiveStartUtc: rollout.effectiveStartUtc ? new Date(rollout.effectiveStartUtc) : null,
      effectiveEndUtc: rollout.effectiveEndUtc ? new Date(rollout.effectiveEndUtc) : null,
    };
  }

  private buildDefinitionUpdateRequest(values: FeatureFlagDefinitionFormValues): FeatureFlagDefinitionUpdateRequest {
    return {
      displayName: values.displayName.trim(),
      description: nullableText(values.description),
      flagTypeCode: values.flagTypeCode.trim(),
      defaultEnabled: values.defaultEnabled,
      ownerCode: values.ownerCode.trim(),
    };
  }

  private buildRolloutUpsertRequest(
    values: FeatureFlagRolloutFormValues,
    effectiveStartUtc: string,
    effectiveEndUtc: string | null,
  ): FeatureFlagRolloutUpsertRequest {
    return {
      scopeTypeCode: values.scopeTypeCode.trim(),
      scopeKey: nullableText(values.scopeKey),
      audienceCode: nullableText(values.audienceCode),
      environmentCode: nullableText(values.environmentCode),
      enabledStateCode: values.enabledStateCode.trim(),
      precedenceRank: Number(values.precedenceRank ?? 0),
      rollbackPostureCode: values.rollbackPostureCode.trim(),
      effectiveStartUtc,
      effectiveEndUtc,
    };
  }

  private validateRolloutFormValues(
    values: FeatureFlagRolloutFormValues,
    effectiveStartUtc: string | null,
    effectiveEndUtc: string | null,
  ): string[] {
    const messages: string[] = [];
    const normalizedScopeType = normalizeCode(values.scopeTypeCode);
    const normalizedScopeKey = normalizeCode(values.scopeKey);

    if (!effectiveStartUtc) {
      messages.push("Effective start must be a valid date and time.");
    }

    if (hasDateValue(values.effectiveEndUtc) && !effectiveEndUtc) {
      messages.push("Effective end must be a valid date and time.");
    }

    if (normalizedScopeType === "global" && normalizedScopeKey.length > 0) {
      messages.push("Scope key must be blank for global rollouts.");
    }

    if (normalizedScopeType.length > 0 && normalizedScopeType !== "global" && normalizedScopeKey.length === 0) {
      messages.push("Scope key is required for non-global rollouts.");
    }

    if (effectiveEndUtc && effectiveStartUtc && new Date(effectiveEndUtc).getTime() < new Date(effectiveStartUtc).getTime()) {
      messages.push("Effective end must be greater than or equal to the effective start.");
    }

    return messages;
  }

  private normalizeRolloutDates(values: FeatureFlagRolloutFormValues): {
    effectiveStartUtc: string | null;
    effectiveEndUtc: string | null;
  } {
    return {
      effectiveStartUtc: this.toIsoUtc(values.effectiveStartUtc),
      effectiveEndUtc: this.toIsoUtc(values.effectiveEndUtc),
    };
  }

  private toIsoUtc(value: string | Date | null): string | null {
    if (!value) {
      return null;
    }

    const candidate = value instanceof Date ? value : new Date(value);
    return Number.isNaN(candidate.getTime()) ? null : candidate.toISOString();
  }

  private resetRolloutEditor(clearMessages = true): void {
    if (clearMessages) {
      this.errorState.set(null);
    }

    this.editingRolloutId.set(null);
    this.rolloutFormValues = this.createEmptyRolloutFormValues();
    this.rolloutForm?.clearErrors();
  }

  private clearMessages(): void {
    this.successMessage.set(null);
    this.errorState.set(null);
  }
}

function compareDefinitions(left: FeatureFlagDefinitionSummary, right: FeatureFlagDefinitionSummary): number {
  return `${left.displayName}|${left.flagCode}`.localeCompare(`${right.displayName}|${right.flagCode}`);
}

function normalizeCode(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function nullableText(value: string | null | undefined): string | null {
  const normalized = (value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function hasDateValue(value: string | Date | null): boolean {
  if (value instanceof Date) {
    return true;
  }

  return typeof value === "string" && value.trim().length > 0;
}
